import React, { useState, useEffect } from 'react';
import { FiFileText, FiEye, FiCheckCircle, FiXCircle, FiClock, FiX, FiAlertCircle } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const C = { text: '#1e293b', sub: '#64748b', border: '#e2e8f0', bg: '#f8fafc', primary: '#10b981', orange: '#f59e0b', red: '#ef4444', blue: '#3b82f6' };

const getStatusStyle = (status) => {
    switch (status) {
        case 'approved': return { color: C.primary, bg: '#d1fae5', icon: <FiCheckCircle /> };
        case 'rejected': return { color: C.red, bg: '#fee2e2', icon: <FiXCircle /> };
        case 'submitted': return { color: C.blue, bg: '#dbeafe', icon: <FiClock /> };
        default: return { color: C.orange, bg: '#fef3c7', icon: <FiFileText /> };
    }
};

const ExamControllerReviewPapers = () => {
    const [papers, setPapers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null); // holds paper + questions when viewing
    const [actionLoading, setActionLoading] = useState(false);
    const [filter, setFilter] = useState('submitted'); // default to pending review
    const [search, setSearch] = useState('');

    const fetchPapers = async () => {
        setLoading(true);
        try {
            // Exam controller uses their own route
            const res = await api.get('/api/exam-controller/papers');
            setPapers(res.data.papers || []);
        } catch (err) {
            console.error('Error fetching papers:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPapers(); }, []);

    const handlePreview = async (id) => {
        try {
            const res = await api.get(`/api/exam-controller/papers/${id}`);
            const p = res.data.paper;
            p.questions = res.data.questions || [];
            setPreview(p);
        } catch (err) {
            alert('Error loading paper preview');
        }
    };

    const handleReview = async (paperId, action) => {
        if (!window.confirm(`Are you sure you want to ${action} this paper?`)) return;
        setActionLoading(true);
        try {
            await api.put(`/api/exam-controller/papers/${paperId}/review`, { action });
            setPreview(null);
            fetchPapers();
        } catch (err) {
            alert(err.response?.data?.message || `Error trying to ${action} paper`);
        } finally {
            setActionLoading(false);
        }
    };

    const filteredPapers = papers.filter(p => {
        const matchesFilter = filter === 'all' || p.status === filter;
        const q = search.toLowerCase();
        const matchesSearch = !search || 
            p.title?.toLowerCase().includes(q) || 
            p.subject_name?.toLowerCase().includes(q) || 
            p.subject_code?.toLowerCase().includes(q) || 
            (p.teacher_name || p.teacher_username)?.toLowerCase().includes(q);
        return matchesFilter && matchesSearch;
    });

    return (
        <div style={{ fontFamily: font, maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ fontSize: 28, fontWeight: 900, color: C.text, margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FiFileText color={C.blue} /> Review Question Papers
                    </h1>
                    <p style={{ margin: 0, color: C.sub, fontSize: 16 }}>Approve or reject papers submitted by teachers before they go live.</p>
                </div>
                <div style={{ position: 'relative', width: '100%', maxWidth: 350 }}>
                    <input 
                        type="text" 
                        placeholder="🔍 Search title, subject, teacher..." 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: '100%', padding: '12px 16px', paddingRight: 40, borderRadius: 12, border: `1.5px solid ${C.border}`, fontSize: 14, fontFamily: font, outline: 'none', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    />
                    {search && (
                        <button 
                            onClick={() => setSearch('')} 
                            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 18, fontWeight: 800 }}
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 24, overflowX: 'auto', paddingBottom: 4 }}>
                {['submitted', 'approved', 'rejected', 'all'].map(f => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        style={{
                            padding: '8px 20px', borderRadius: 20, fontSize: 13, fontWeight: 700, fontFamily: font, textTransform: 'capitalize', cursor: 'pointer', border: 'none', transition: 'all 0.2s',
                            background: filter === f ? C.text : '#fff', color: filter === f ? '#fff' : C.sub, boxShadow: filter === f ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 6px rgba(0,0,0,0.02)'
                        }}
                    >
                        {f === 'submitted' ? 'Pending Review' : f}
                        <span style={{ marginLeft: 6, opacity: 0.8 }}>({papers.filter(p => f === 'all' || p.status === f).length})</span>
                    </button>
                ))}
            </div>

            {/* List */}
            {loading ? (
                <div style={{ padding: 60, textAlign: 'center', color: C.sub, fontWeight: 600 }}>Loading papers...</div>
            ) : filteredPapers.length === 0 ? (
                <div style={{ background: '#fff', borderRadius: 24, padding: 60, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                    <FiCheckCircle size={48} color="#d1fae5" style={{ marginBottom: 16, color: C.primary }} />
                    <h3 style={{ margin: '0 0 8px', fontSize: 18, color: C.text, fontWeight: 800 }}>All caught up!</h3>
                    <p style={{ margin: 0, color: C.sub }}>No papers currently matching this status.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {filteredPapers.map(p => {
                        const st = getStatusStyle(p.status);
                        return (
                            <div key={p.id} style={{ background: '#fff', borderRadius: 20, padding: '20px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 20, transition: 'transform 0.2s', borderLeft: `6px solid ${st.color}` }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>{p.title}</h3>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 12, background: st.bg, color: st.color, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            {st.icon} {p.status}
                                        </span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                                        <div style={{ fontSize: 13, color: C.sub }}><strong>Subject:</strong> {p.subject_name} ({p.subject_code})</div>
                                        <div style={{ fontSize: 13, color: C.sub }}><strong>Teacher:</strong> {p.teacher_name || p.teacher_username}</div>
                                        <div style={{ fontSize: 13, color: C.sub }}><strong>Dept:</strong> {p.department}</div>
                                        <div style={{ fontSize: 13, color: C.sub }}><strong>Batch:</strong> {p.batch_name}</div>
                                        <div style={{ fontSize: 13, color: C.sub }}><strong>Stats:</strong> {p.question_count} Qs • {p.total_marks} Marks • {p.duration_mins}m</div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handlePreview(p.id)}
                                    style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: '#f1f5f9', color: C.text, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, transition: 'all 0.2s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
                                >
                                    <FiEye /> View & Review
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Preview & Review Modal */}
            {preview && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(6px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: '#fff', borderRadius: 24, width: '100%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 48px rgba(0,0,0,0.2)', animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>

                        {/* Modal Header */}
                        <div style={{ padding: '24px 32px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderRadius: '24px 24px 0 0' }}>
                            <div>
                                <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 900, color: C.text }}>{preview.title}</h2>
                                <div style={{ fontSize: 13, color: C.sub, display: 'flex', gap: 12 }}>
                                    <span>{preview.subject_name} ({preview.subject_code})</span>
                                    <span>•</span>
                                    <span>By {preview.teacher_name || preview.teacher_username}</span>
                                </div>
                            </div>
                            <button onClick={() => setPreview(null)} style={{ background: '#e2e8f0', border: 'none', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.sub, transition: 'all 0.2s' }}>
                                <FiX size={20} />
                            </button>
                        </div>

                        {/* Modal Body (Questions) */}
                        <div style={{ padding: 32, overflowY: 'auto', flex: 1, background: '#fff' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32, padding: 20, background: '#f1f5f9', borderRadius: 16 }}>
                                <div><span style={{ display: 'block', fontSize: 11, color: C.sub, textTransform: 'uppercase', fontWeight: 800, marginBottom: 4 }}>Total Marks</span><strong style={{ fontSize: 20, color: C.text }}>{preview.total_marks}</strong></div>
                                <div><span style={{ display: 'block', fontSize: 11, color: C.sub, textTransform: 'uppercase', fontWeight: 800, marginBottom: 4 }}>Duration</span><strong style={{ fontSize: 20, color: C.text }}>{preview.duration_mins} mins</strong></div>
                                <div><span style={{ display: 'block', fontSize: 11, color: C.sub, textTransform: 'uppercase', fontWeight: 800, marginBottom: 4 }}>Questions</span><strong style={{ fontSize: 20, color: C.text }}>{preview.questions?.length || 0}</strong></div>
                            </div>

                            <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 20, color: C.text, borderBottom: `2px solid ${C.border}`, paddingBottom: 10 }}>Question Paper Content</h3>

                            {preview.questions?.length === 0 ? (
                                <p style={{ color: C.sub, fontStyle: 'italic', textAlign: 'center', padding: 40 }}>No questions found in this paper.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                    {preview.questions.map((q, i) => (
                                        <div key={i} style={{ padding: 20, border: `1px solid ${C.border}`, borderRadius: 16, background: '#fafafa' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                                <div style={{ fontWeight: 700, fontSize: 15, color: C.text, lineHeight: 1.5 }}>
                                                    <span style={{ color: C.blue, marginRight: 8 }}>Q{q.question_number}.</span>
                                                    {q.question_text}
                                                </div>
                                                <span style={{ background: '#e2e8f0', color: C.text, fontSize: 12, fontWeight: 800, padding: '4px 10px', borderRadius: 12 }}>{q.marks} Marks</span>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16 }}>
                                                {['A', 'B', 'C', 'D'].map(opt => {
                                                    const text = q[`option_${opt.toLowerCase()}`];
                                                    if (!text) return null;
                                                    const isCorrect = q.correct_option === opt;
                                                    return (
                                                        <div key={opt} style={{ padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${isCorrect ? C.primary : C.border}`, background: isCorrect ? '#ecfdf5' : '#fff', display: 'flex', gap: 10, alignItems: 'center' }}>
                                                            <div style={{ width: 24, height: 24, borderRadius: '50%', background: isCorrect ? C.primary : '#f1f5f9', color: isCorrect ? '#fff' : C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{opt}</div>
                                                            <span style={{ fontSize: 14, color: isCorrect ? '#065f46' : C.text, fontWeight: isCorrect ? 600 : 400 }}>{text}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer (Actions) */}
                        <div style={{ padding: '20px 32px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: '0 0 24px 24px' }}>
                            <div style={{ fontSize: 13, color: C.sub, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FiAlertCircle /> Review carefully before approving.
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    onClick={() => handleReview(preview.id, 'reject')}
                                    disabled={actionLoading || preview.status === 'rejected'}
                                    style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: '#fee2e2', color: C.red, fontWeight: 800, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: (actionLoading || preview.status === 'rejected') ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s' }}
                                >
                                    <FiXCircle /> {preview.status === 'rejected' ? 'Rejected' : 'Reject Paper'}
                                </button>
                                <button
                                    onClick={() => handleReview(preview.id, 'approve')}
                                    disabled={actionLoading || preview.status === 'approved'}
                                    style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: C.primary, color: '#fff', fontWeight: 800, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: (actionLoading || preview.status === 'approved') ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)' }}
                                >
                                    <FiCheckCircle /> {preview.status === 'approved' ? 'Approved' : 'Approve Paper'}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes slideUp { from { opacity: 0; transform: translateY(30px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
            `}} />
        </div>
    );
};

export default ExamControllerReviewPapers;
